// Notifications and briefs, in words. The bell shows a dot, never a number (invariant 8: no shame
// metrics); only its accessible label says how many, capped at "more than 9".
import type {
  BriefContent,
  BriefCoverage,
  BriefSummary,
  BriefSummaryClaim,
  BriefSummarySection,
  BriefView,
  NotificationView,
} from "@shared/api";
import { formatAgo, formatShortDateTime, type TimeFormatOptions } from "@web/lib/time";

export function bellLabel(unseen: number): string {
  if (!Number.isFinite(unseen) || unseen <= 0) return "Notifications";
  return `Notifications, ${unseen > 9 ? "more than 9" : Math.floor(unseen)} new`;
}

/** How long a brief stays "new" enough for the banner. */
export const BRIEF_BANNER_MS = 12 * 3_600_000;

/** The brief the calm banner offers: the latest one, unopened, recent, and not waved away. */
export function bannerBrief(
  briefs: readonly BriefView[] | undefined,
  now: number,
  dismissed: ReadonlySet<string>,
  maxAgeMs = BRIEF_BANNER_MS,
): BriefView | null {
  const latest = briefs?.reduce<BriefView | null>(
    (best, b) => (!best || b.createdAt > best.createdAt ? b : best),
    null,
  );
  if (!latest || latest.openedAt || dismissed.has(latest.id)) return null;
  // A brief "from the future" is just new: the reader's clock is re-read once a minute and may
  // also differ a little from the server's.
  const age = now - new Date(latest.createdAt).getTime();
  return age <= maxAgeMs ? latest : null;
}

export function briefIsEmpty(content: BriefContent): boolean {
  return (
    content.feeds.every((f) => f.toReview === 0 && f.readyNext === 0) &&
    !content.needsYouNow.length &&
    !content.tasksDue.length &&
    !content.awaitingReply.length &&
    !content.fyi.length &&
    !content.catchUpLater?.senders.length &&
    !content.held?.length
  );
}

/** The summary's sections in reading order (MyMail's brief layout), with their headings. */
export const SUMMARY_SECTIONS: readonly { section: BriefSummarySection; title: string }[] = [
  { section: "needs_you", title: "Needs you" },
  { section: "handled", title: "Handled" },
  { section: "catch_up", title: "Catch up later" },
];

/** The summary's points by section, in reading order, leaving out empty sections. */
export function summaryGroups(
  claims: readonly BriefSummaryClaim[],
): { section: BriefSummarySection; title: string; claims: BriefSummaryClaim[] }[] {
  return SUMMARY_SECTIONS.map((s) => ({
    ...s,
    claims: claims.filter((c) => c.section === s.section),
  })).filter((g) => g.claims.length > 0);
}

/** The one calm line shown instead of a summary. */
export function summaryUnavailableLine(reason: BriefSummary["reason"]): string {
  const rest = "Everything below is complete.";
  switch (reason) {
    case "no_model":
      return `No written summary this time: Claude isn't connected. ${rest}`;
    case "budget":
      return `No written summary this time: Claude is resting until tomorrow's budget. ${rest}`;
    default:
      return `No written summary this time. ${rest}`;
  }
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** What the summary read and how fresh the mail was: "Read 12 threads from 2 accounts since …". */
export function coverageLine(
  coverage: BriefCoverage,
  now: number,
  options: TimeFormatOptions = {},
): string {
  const read = coverage.skipped
    ? `Read ${coverage.threads} of ${plural(coverage.threads + coverage.skipped, "thread")}`
    : `Read ${plural(coverage.threads, "thread")}`;
  const since = formatShortDateTime(coverage.since, options);
  const synced = coverage.syncedAt
    ? `mail synced ${formatAgo(coverage.syncedAt, now)}`
    : "mail hasn't synced yet";
  return `${read} from ${plural(coverage.accounts, "account")} since ${since} · ${synced}`;
}

/** Points the server left out because their quote didn't match the email; null when none. */
export function droppedLine(dropped: number): string | null {
  if (dropped <= 0) return null;
  return dropped === 1
    ? "Left out one point Claude couldn't back up with the email's own words."
    : `Left out ${dropped} points Claude couldn't back up with the email's own words.`;
}

export type NotificationTarget =
  | { kind: "brief"; briefId: string }
  | { kind: "card"; cardId: string }
  | { kind: "task"; taskId: string }
  | { kind: "accounts" };

/** Where opening a notification takes the reader. */
export function notificationTarget(
  n: Pick<NotificationView, "kind" | "briefId" | "cardId" | "taskId" | "accountId">,
): NotificationTarget | null {
  if (n.briefId) return { kind: "brief", briefId: n.briefId };
  if (n.cardId) return { kind: "card", cardId: n.cardId };
  if (n.taskId) return { kind: "task", taskId: n.taskId };
  if (n.kind === "health" || n.kind === "sync_gap" || n.accountId) return { kind: "accounts" };
  return null;
}

export const NOTIFICATION_KIND_LABEL: Record<NotificationView["kind"], string> = {
  brief: "Brief",
  breakthrough: "Needs you now",
  reminder: "Reminder",
  health: "Account",
  sync_gap: "Sync",
};
